import i18next from 'i18next'
import 'reflect-metadata'
import {inject, injectable} from 'inversify'
import {makeAutoObservable, runInAction} from 'mobx'
import {openErrorNotification} from '@/utils'
import {SetPasswordRequest} from '@/stores'
import {IChangeOldPasswordStore} from '@/pages/ChangeOldPassword'
import {ConfirmEmailStoreSymbol} from '@/pages/ConfirmEmail/ConfirmEmail'
import {TConfirmEmailStore} from '@/pages/ConfirmEmail'
import {ResetPasswordStoreSymbol} from '@/pages/ResetPassword/ResetPassword'
import {TResetPasswordStore} from '@/pages/ResetPassword'
import {fetchSetPassword} from './fetchers'
import {AuthType} from './models'
import {LoadingState} from '@/types'
import {ROUTES} from '@/router/routes'
import {getRouterStore} from '@/router/utils'
@injectable()
export class ChangeOldPasswordStore implements IChangeOldPasswordStore {
    private _confirmEmailStore: TConfirmEmailStore
    private _resetPasswordStore: TResetPasswordStore

    constructor(
        @inject(ConfirmEmailStoreSymbol) confirmEmailStore: TConfirmEmailStore,
        @inject(ResetPasswordStoreSymbol) resetPasswordStore: TResetPasswordStore
    ) {
        makeAutoObservable(this)

        this._confirmEmailStore = confirmEmailStore
        this._resetPasswordStore = resetPasswordStore
    }

    public restrictionErrors = {
        betweenEightAndFiftyCharacters: true,
        atLeastOneDigit: true,
        atLeastOneSpecialCharacter: true,
        atLeastOneUppercaseLetter: true,
        atLeastOneLowercaseLetter: true,
        onlyLatinCharacters: true
    }
    public password = ''
    public passwordChangingLoadingState = LoadingState.IDLE

    get email() {
        return this._confirmEmailStore.email
    }

    get verificationId() {
        return this._confirmEmailStore.verificationId
    }

    changePassword = async () => {
        const request: SetPasswordRequest = {
            email: this.email,
            password: this.password,
            verificationId: this.verificationId
        }

        runInAction(() => {
            this.passwordChangingLoadingState = LoadingState.LOADING
        })

        try {
            const {error, status} = await fetchSetPassword(request)

            if (status === 401) {
                getRouterStore().push(ROUTES.confirmTwoFA)
                return
            }

            if (error) {
                runInAction(() => {
                    this.passwordChangingLoadingState = LoadingState.FAILED
                })
                openErrorNotification(error.message)
            } else {
                runInAction(() => {
                    this.passwordChangingLoadingState = LoadingState.DONE
                })

                this._resetPasswordStore.clearStoredEmail()
                getRouterStore().push(ROUTES.setPasswordSuccess)
            }
        } catch (error) {
            runInAction(() => {
                this.passwordChangingLoadingState = LoadingState.FAILED
            })
            openErrorNotification(i18next.t('set-password.errors.general'))
        }
    }

    confirmTwoFA = async (code: number, authType: AuthType) => {
        const request: SetPasswordRequest = {
            email: this.email,
            password: this.password,
            verificationId: this.verificationId
        }

        runInAction(() => {
            this.passwordChangingLoadingState = LoadingState.LOADING
        })

        try {
            const {error} = await fetchSetPassword(request, code, authType)

            if (error) {
                runInAction(() => {
                    this.passwordChangingLoadingState = LoadingState.FAILED
                })
                openErrorNotification(error.message)
            } else {
                runInAction(() => {
                    this.passwordChangingLoadingState = LoadingState.DONE
                })

                this._resetPasswordStore.clearStoredEmail()
                getRouterStore().push(ROUTES.setPasswordSuccess)
            }
        } catch (error) {
            runInAction(() => {
                this.passwordChangingLoadingState = LoadingState.FAILED
            })
            openErrorNotification(i18next.t('set-password.errors.general'))
        }
    }

    setPassword = (password: string) => {
        this.password = password
        this.restrictionErrors = {
            betweenEightAndFiftyCharacters: password.length < 8 || password.length > 50,
            atLeastOneDigit: !/\d/.test(password),
            atLeastOneSpecialCharacter: !/[^a-zA-Z0-9]/.test(password),
            atLeastOneUppercaseLetter: !/[A-Z]/.test(password),
            atLeastOneLowercaseLetter: !/[a-z]/.test(password),
            onlyLatinCharacters:
                password.length === 0 || !/[a-zA-Z]/.test(password) || /[^\x20-\x7E]/.test(password)
        }
    }

    continue = () => {
        getRouterStore().push(ROUTES.login)
    }
}
